
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useExpiringLicenses } from "@/hooks/useExpiringLicenses";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const NotificationsMenu = () => {
  const navigate = useNavigate();
  const { expiringGuards, expiringCount } = useExpiringLicenses();
  const { isAdmin } = useUserRoles();
  const [teamMessages, setTeamMessages] = useState<any[]>([]);
  const [totalNotifications, setTotalNotifications] = useState(0);

  useEffect(() => {
    const fetchRecentMessages = async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        if (!userData?.user) return;
        
        const { data: userTeams } = await supabase
          .from('team_members')
          .select('team_id')
          .eq('profile_id', userData.user.id);
          
        if (!userTeams || userTeams.length === 0) return;
        
        const teamIds = userTeams.map(t => t.team_id);
        const { data: messages } = await supabase
          .from('team_messages')
          .select(`
            id, 
            content, 
            created_at, 
            team_id,
            author_id,
            teams(name)
          `)
          .in('team_id', teamIds)
          .neq('author_id', userData.user.id)
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (messages && messages.length > 0) {
          const authorIds = [...new Set(messages.map(m => m.author_id))];
          const { data: authors } = await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', authorIds);
            
          const authorsMap = new Map();
          if (authors) {
            authors.forEach(author => {
              authorsMap.set(author.id, author);
            });
          }
          
          const formattedMessages = messages.map(msg => ({
            ...msg,
            author: authorsMap.get(msg.author_id) || { full_name: 'Team Member' }
          }));
          
          setTeamMessages(formattedMessages);
        }
        
        setTotalNotifications((isAdmin ? expiringCount : 0) + (messages?.length || 0));
      } catch (error) {
        console.error("Error fetching recent messages:", error);
      }
    };
    
    fetchRecentMessages();
    
    const channel = supabase
      .channel(`notifications_channel_${Date.now()}`) // Make channel name unique
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'team_messages'
      }, payload => {
        console.log("New message notification:", payload);
        fetchRecentMessages();
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, expiringCount]);

  const handleAlertsClick = () => {
    if (isAdmin) {
      navigate('/alerts');
    }
  };
  
  const handleMessageClick = (teamId: string) => {
    navigate(`/applications/team/${teamId}/chat`);
  };

  return (
    <DropdownMenu>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-gray-600 relative dark:text-gray-300">
                <Bell className="h-5 w-5" />
                {totalNotifications > 0 && (
                  <Badge 
                    variant="destructive" 
                    className="absolute top-0 right-0 w-5 h-5 flex items-center justify-center p-0 text-[10px] rounded-full bg-red-600 text-white"
                  >
                    {totalNotifications}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>Notifications{totalNotifications > 0 ? ` (${totalNotifications})` : ""}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" className="w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg z-50">
        {isAdmin && expiringCount > 0 ? (
          <>
            <div className="px-2 py-1.5 text-sm font-medium text-gray-500 border-b">
              Guards with Expiring Licenses
            </div>
            {expiringGuards.map((guard) => (
              <DropdownMenuItem 
                key={guard.id}
                onClick={handleAlertsClick}
                className="flex flex-col items-start p-2 cursor-pointer hover:bg-gray-100"
              >
                <div className="font-medium">
                  {guard.first_name} {guard.last_name}
                </div>
                <div className="text-sm text-gray-500">
                  Expires: {format(new Date(guard.license_expiry_date), 'dd/MM/yyyy')}
                </div>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        
        {teamMessages.length > 0 ? (
          <>
            <div className="px-2 py-1.5 text-sm font-medium text-gray-500 border-b">
              Recent Messages
            </div>
            {teamMessages.map((message) => (
              <DropdownMenuItem 
                key={message.id}
                onClick={() => handleMessageClick(message.team_id)}
                className="flex flex-col items-start p-2 cursor-pointer hover:bg-gray-100"
              >
                <div className="flex justify-between w-full">
                  <span className="font-medium">{message.author.full_name}</span>
                  <span className="text-xs text-gray-400">
                    {format(new Date(message.created_at), 'dd/MM/yyyy')}
                  </span>
                </div>
                <div className="text-sm text-gray-500 truncate w-full">
                  {message.teams?.name}: {message.content || 'Image message'}
                </div>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
        
        {totalNotifications === 0 && (
          <DropdownMenuItem disabled className="text-center py-3">
            No notifications
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
